/* ==========================================================================
   Clínica Vie Et Santé — 03 Hero
   Carrusel: avance automático pausable (WCAG 2.2.2), inert en las láminas
   ocultas, y respeto por prefers-reduced-motion.
   ========================================================================== */

class VesHero {
  constructor(root) {
    this.root = root;
    this.slides = Array.from(root.querySelectorAll('[data-slide]'));
    this.copies = Array.from(root.querySelectorAll('[data-copy]'));
    this.dots = Array.from(root.querySelectorAll('[data-dot]'));
    this.pauseBtn = root.querySelector('[data-pause]');
    if (this.slides.length < 1) return;

    this.index = 0;
    this.timer = null;
    this.userPaused = false;
    this.visible = false;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.speed = parseInt(root.dataset.speed, 10) || 6000;
    // Un carrusel que avanza solo es justo lo que la preferencia de menos
    // movimiento evita. Ahí quedan los controles y el visitante decide.
    this.autoplay = root.dataset.autoplay === 'true' && this.slides.length > 1 && !this.reduced;

    this.playVideo(0);
    this.bind();
    this.observe();

    // Las láminas 2+ salen del HTML con el src en data-: así no compiten con el
    // LCP. Se hidratan cuando la página terminó de cargar —ya nada urgente en
    // la red— y también justo antes de mostrarse, por si alguien avanza antes.
    if (document.readyState === 'complete') this.hydrate();
    else window.addEventListener('load', () => this.hydrate(), { once: true });
  }

  hydrate(slide) {
    const alcance = slide || this.root;
    alcance.querySelectorAll('img[data-ves-defer]').forEach((img) => {
      if (img.dataset.srcset) img.srcset = img.dataset.srcset.trim();
      if (img.dataset.src) img.src = img.dataset.src;
      img.removeAttribute('data-ves-defer');
    });
  }

  go(i) {
    if (i === this.index) return;

    this.slides[this.index].classList.remove('is-active');
    this.pauseVideo(this.index);

    this.index = i;

    // Por si el visitante avanza antes de que la página termine de cargar: la
    // lámina que va a verse se hidrata primero, no llega vacía.
    this.hydrate(this.slides[i]);

    this.slides[i].classList.add('is-active');
    this.playVideo(i);

    this.copies.forEach((c, n) => {
      const on = n === i;
      c.classList.toggle('is-active', on);
      // inert: sin esto los enlaces de las láminas ocultas siguen siendo
      // tabulables y el foco se va a algo que nadie ve.
      if (on) c.removeAttribute('inert');
      else c.setAttribute('inert', '');
    });

    this.dots.forEach((d, n) => {
      d.classList.toggle('is-active', n === i);
      d.setAttribute('aria-selected', String(n === i));
    });
  }

  videoAt(i) {
    return this.slides[i] ? this.slides[i].querySelector('video') : null;
  }

  playVideo(i) {
    const v = this.videoAt(i);
    if (!v || this.reduced) return;
    v.muted = true;
    v.playsInline = true;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }

  pauseVideo(i) {
    const v = this.videoAt(i);
    if (v) v.pause();
  }

  next() {
    this.go((this.index + 1) % this.slides.length);
  }

  bind() {
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.go(i);
        this.restart();
      });
    });

    if (this.pauseBtn) {
      this.pauseBtn.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.pauseBtn.setAttribute('aria-pressed', String(this.userPaused));
        if (this.userPaused) this.stop();
        else this.start();
      });
    }

    // El puntero y el teclado mandan sobre el avance: si alguien está
    // leyendo, no se lo movemos debajo.
    this.root.addEventListener('pointerenter', () => this.stop());
    this.root.addEventListener('pointerleave', () => this.start());
    this.root.addEventListener('focusin', () => this.stop());
    this.root.addEventListener('focusout', (e) => {
      if (!this.root.contains(e.relatedTarget)) this.start();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stop();
      else if (this.visible) this.start();
    });
  }

  observe() {
    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          this.visible = e.isIntersecting;
          if (e.isIntersecting) this.start();
          else this.stop();
        });
      },
      { threshold: 0.2 }
    );
    this.io.observe(this.root);
  }

  restart() {
    this.stop();
    this.start();
  }

  start() {
    if (!this.autoplay || this.userPaused || this.timer || !this.visible) return;
    this.timer = setInterval(() => this.next(), this.speed);
  }

  stop() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }
}

function initVesHero() {
  document.querySelectorAll('[data-ves-hero]').forEach((el) => {
    if (el.dataset.vesInit === 'true') return;
    el.dataset.vesInit = 'true';
    new VesHero(el);
  });
}

document.addEventListener('DOMContentLoaded', initVesHero);
document.addEventListener('shopify:section:load', initVesHero);
